import React, { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import { db } from "../Firebase";
import {
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  doc as firestoreDoc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { createLetters } from "./letterService";
import LoginPopup from "./LoginPopup";
import UserOptionsMenu from "./UserOptionsMenu";
import { FaSignInAlt } from "react-icons/fa";
import "./Dashboard.css";
import "./CreateLetters.css";

Modal.setAppElement("#root");

function EditableNote({ initialValue, onUpdate, isLoggedIn }) {
  const [value, setValue] = useState(initialValue);

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleBlur = () => {
    if (value !== initialValue && isLoggedIn) {
      onUpdate(value);
    }
  };

  return (
    <textarea
      value={value || ""}
      onChange={handleChange}
      onBlur={handleBlur}
      className="note-textarea"
      placeholder={isLoggedIn ? "Add a note..." : "Login to add notes"}
      disabled={!isLoggedIn}
    />
  );
}

function Dashboard() {
  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [addressFilter, setAddressFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Available");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [userNotes, setUserNotes] = useState({});
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user);
      if (user) {
        fetchUserData(user);
      } else {
        setTemplates([]);
        setUserNotes({});
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchUserData = async (user) => {
    try {
      const templatesQuery = query(
        collection(db, "templates"),
        where("userId", "==", user.uid)
      );
      const templatesSnapshot = await getDocs(templatesQuery);
      const templatesList = templatesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTemplates(templatesList);

      const userNotesRef = collection(db, "userNotes", user.uid, "notes");
      const userNotesSnapshot = await getDocs(userNotesRef);
      const notesData = {};
      userNotesSnapshot.forEach((doc) => {
        notesData[doc.id] = doc.data().note;
      });
      setUserNotes(notesData);
    } catch (error) {
      console.error("Error fetching user data: ", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "properties"),
      (snapshot) => {
        const propertiesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setData(propertiesData);
      },
      (error) => {
        console.error("Error fetching properties: ", error);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleNoteUpdate = async (propertyId, newNote) => {
    if (!isLoggedIn) {
      setIsLoginPopupOpen(true);
      return;
    }

    try {
      if (user) {
        const noteRef = firestoreDoc(
          db,
          "userNotes",
          user.uid,
          "notes",
          propertyId
        );
        await setDoc(noteRef, { note: newNote }, { merge: true });
        setUserNotes((prevNotes) => ({
          ...prevNotes,
          [propertyId]: newNote,
        }));
      }
    } catch (error) {
      console.error("Error updating note:", error);
      alert("Failed to update note. Please try again.");
    }
  };

  const handleSelectAllChange = () => {
    if (!selectAll) {
      setSelectedProperties(data);
    } else {
      setSelectedProperties([]);
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (property) => {
    setSelectedProperties((prevSelected) =>
      prevSelected.includes(property)
        ? prevSelected.filter((p) => p.id !== property.id)
        : [...prevSelected, property]
    );
  };

  const handleShowAddress = (property) => {
    const fullAddress = `${property.houseNumber || ""}, ${
      property.roadName || ""
    }\n${property.town || ""}\n${property.county || ""}\n${
      property.postcode || ""
    }`;
    navigator.clipboard.writeText(fullAddress).then(() => {
      alert("Address copied to clipboard!");
    });
  };

  const handleCreateLetters = () => {
    if (!isLoggedIn) {
      setIsLoginPopupOpen(true);
      return;
    }
    setIsModalOpen(true);
  };

  const handleSelectTemplate = async (template) => {
    setIsModalOpen(false);
    try {
      await createLetters(template, selectedProperties);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUploadTemplate = () => {
    if (!isLoggedIn) {
      setIsLoginPopupOpen(true);
      return;
    }
    navigate("/upload-template");
  };

  const handleLogin = () => {
    setIsLoginPopupOpen(true);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsLoggedIn(false);
      setTemplates([]);
      setUserNotes({});
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleAdminLogin = () => {
    navigate("/admin-login");
  };

  const filteredData = useMemo(() => {
    return data.filter(
      (property) =>
        (property.roadName || "")
          .toLowerCase()
          .includes(addressFilter.toLowerCase()) &&
        (property.agent || "")
          .toLowerCase()
          .includes(agentFilter.toLowerCase()) &&
        (statusFilter === "" || property.status === statusFilter)
    );
  }, [data, addressFilter, agentFilter, statusFilter]);

  const propertyCount = filteredData.length;

  const columns = useMemo(
    () => [
      {
        Header: (
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAllChange}
            className="select-all-checkbox"
          />
        ),
        accessor: "select",
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedProperties.includes(row.original)}
            onChange={() => handleCheckboxChange(row.original)}
          />
        ),
        id: "checkbox",
      },
      {
        Header: "Address",
        accessor: (row) => `${row.houseNumber || ""}, ${row.roadName || ""}`,
        id: "address",
        Cell: ({ row }) => (
          <div className="address-container">
            {`${row.original.houseNumber || ""}, ${
              row.original.roadName || ""
            }`}
            <button
              className="show-link"
              onClick={() => handleShowAddress(row.original)}
            >
              Copy
            </button>
          </div>
        ),
      },
      {
        Header: "Asking Price",
        accessor: "askingPrice",
        Cell: ({ value }) => <span>£{(value || 0).toLocaleString()}</span>,
        sortType: "basic",
      },
      {
        Header: "Agent",
        accessor: "agent",
      },
      {
        Header: "Weeks on Market",
        accessor: "weeksOnMarket",
        sortType: "basic",
      },
      {
        Header: "Rightmove Link",
        accessor: "rightmoveLink",
        Cell: ({ value }) => (
          <a
            href={value || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="link-button"
          >
            View Listing
          </a>
        ),
      },
      {
        Header: "Last Letter Date",
        accessor: "lastLetterDate",
        Cell: ({ value }) => <span>{value || "N/A"}</span>,
        sortType: "basic",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => <span>{value || "N/A"}</span>,
      },
      {
        Header: "Notes",
        accessor: "id",
        Cell: ({ value: propertyId }) => (
          <EditableNote
            initialValue={userNotes[propertyId] || ""}
            onUpdate={(newNote) => handleNoteUpdate(propertyId, newNote)}
            isLoggedIn={isLoggedIn}
          />
        ),
      },
    ],
    [
      selectAll,
      selectedProperties,
      addressFilter,
      agentFilter,
      userNotes,
      isLoggedIn,
    ]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: filteredData }, useSortBy);

  return (
    <div className="dashboard-container">
      <div className="dashboard-header centered">
        <h1 className="dashboard-heading">
          <span className="highlight">{propertyCount}</span> properties in{" "}
          <span className="highlight">Marlow</span>
        </h1>
        {isLoggedIn ? (
          <UserOptionsMenu
            onLogout={handleLogout}
            onAdminLogin={handleAdminLogin}
          />
        ) : (
          <button className="login-trigger" onClick={handleLogin}>
            <FaSignInAlt size={24} />
          </button>
        )}
      </div>

      <div className="filter-container">
        <label htmlFor="statusFilter">Filter by Status: </label>
        <select
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter-dropdown"
        >
          <option value="">All</option>
          {[...new Set(data.map((property) => property.status))].map(
            (status) => (
              <option key={status} value={status}>
                {status}
              </option>
            )
          )}
        </select>
      </div>

      <p className="dashboard-subtext">
        Select the properties you want to send a letter to, and click 'create
        letters'. It's pretty self explanatory really
      </p>

      {selectedProperties.length > 0 && (
        <div className="action-button-container show">
          <button className="action-button" onClick={handleCreateLetters}>
            Create Letters
          </button>
        </div>
      )}

      <table {...getTableProps()} className="property-table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr
              key={headerGroup.id}
              {...headerGroup.getHeaderGroupProps()}
              className="table-header"
            >
              {headerGroup.headers.map((column) => (
                <th
                  key={column.id}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render("Header")}
                  {column.isSorted ? (
                    column.isSortedDesc ? (
                      <span> 🔽</span>
                    ) : (
                      <span> 🔼</span>
                    )
                  ) : (
                    ""
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr key={row.id} {...row.getRowProps()} className="table-row">
                {row.cells.map((cell) => (
                  <td key={cell.id} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasMore && <p className="loading-more">Loading more properties...</p>}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Select a Template"
        className="template-modal"
        overlayClassName="modal-overlay"
      >
        <h2>Select a Template for Letters</h2>
        <ul className="template-list">
          {templates.length === 0 ? (
            <li>No templates available. Please upload a template first.</li>
          ) : (
            templates.map((template) => (
              <li
                key={template.id}
                className="template-item"
                onClick={() => handleSelectTemplate(template)}
              >
                {template.name}
              </li>
            ))
          )}
        </ul>
      </Modal>

      <LoginPopup
        isOpen={isLoginPopupOpen}
        onRequestClose={() => setIsLoginPopupOpen(false)}
        onLogin={() => {
          setIsLoginPopupOpen(false);
          setIsLoggedIn(true);
        }}
      />
    </div>
  );
}

export default Dashboard;
